import useSearchCondition, {
  SearchConditionResponse,
} from "@/hooks/useSearchCondition";
import { ProductSearchQueryParameter } from "@/types/ProductSearchQueryParameter";
import { CategoryResponse, MaterialResponse } from "@/web-client/api";
import {
  foramtFireProtectionMaterial,
  formatFireRegistance,
  formatFlamePreventionRegulation,
  formatFormaldehyde,
} from "@/utils/formatProductDetail";
import Colors from "@/consts/Colors";
import useMaterials from "@/hooks/useMaterials";
import formatNumber from "@/utils/formatNumber";
import { SizeRangeSliderUnit } from "@/consts/RangeSlider";
import useFindEntityWithSubEntities from "@/hooks/useFindEntityWithSubEntitites";
import { useSelectedSearchConditionGroups } from "@/features/search_product";

const useProductSearchCondition = (
  query: ProductSearchQueryParameter,
  categories: CategoryResponse[],
): SearchConditionResponse<ProductSearchQueryParameter> => {
  const materials = useMaterials();
  const {
    selectedEntity: selectedCategory,
    parentEntities: categoryParentEntities,
  } = useFindEntityWithSubEntities<CategoryResponse>(
    categories,
    query.category_id,
  );

  const {
    selectedEntity: selectedMaterial,
    parentEntities: materialParentEntities,
  } = useFindEntityWithSubEntities<MaterialResponse>(
    materials,
    query.material_id,
  );

  // 現在検索中の詳細検索項目グループを取得
  const { selectedSearchConditionGroups } = useSelectedSearchConditionGroups(
    query.search_condition_value_ids
      ? query.search_condition_value_ids.split(",").map((v) => Number(v))
      : [],
    query.category_id ? Number(query.category_id) : undefined,
  );

  const searchConditions = useSearchCondition<ProductSearchQueryParameter>(
    (conditions) => {
      if (query.keyword) {
        conditions.push({
          key: "keyword",
          label: `キーワード: ${query.keyword}`,
          value: query.keyword,
        });
      }

      if (query.colors) {
        const colors = query.colors.split(",");
        colors.map((v) => {
          const targetColor = Colors.find((color) => color.id.toString() === v);
          conditions.push({
            key: "colors",
            label: `カラー: ${targetColor.name}`,
            value: v,
          });
        });
      }

      if (query.formaldehyde) {
        conditions.push({
          key: "formaldehyde",
          label: `ホルムアルデヒド等級: ${formatFormaldehyde(
            Number(query.formaldehyde),
          )}`,
          value: query.formaldehyde,
        });
      }

      if (query.fire_protection_material) {
        conditions.push({
          key: "fire_protection_material",
          label: `防火材料: ${foramtFireProtectionMaterial(
            Number(query.fire_protection_material),
          )}`,
          value: query.fire_protection_material,
        });
      }

      if (query.flame_prevention_regulation) {
        conditions.push({
          key: "flame_prevention_regulation",
          label: `防炎規制: ${formatFlamePreventionRegulation(
            Number(query.flame_prevention_regulation),
          )}`,
          value: query.flame_prevention_regulation,
        });
      }

      if (query.fire_registance) {
        conditions.push({
          key: "fire_registance",
          label: `耐火性能: ${formatFireRegistance(
            Number(query.fire_registance),
          )}`,
          value: query.fire_registance,
        });
      }

      if (query.min_width) {
        conditions.push({
          key: "min_width",
          label: `幅（最小）: ${formatNumber(
            Number(query.min_width),
          )}${SizeRangeSliderUnit}`,
          value: query.min_width,
        });
      }

      if (query.max_width) {
        conditions.push({
          key: "max_width",
          label: `幅（最大）: ${formatNumber(
            Number(query.max_width),
          )}${SizeRangeSliderUnit}`,
          value: query.max_width,
        });
      }

      if (query.min_height) {
        conditions.push({
          key: "min_height",
          label: `高さ（最小）: ${formatNumber(
            Number(query.min_height),
          )}${SizeRangeSliderUnit}`,
          value: query.min_height,
        });
      }

      if (query.max_height) {
        conditions.push({
          key: "max_height",
          label: `高さ（最大）: ${formatNumber(
            Number(query.max_height),
          )}${SizeRangeSliderUnit}`,
          value: query.max_height,
        });
      }

      if (query.min_depth) {
        conditions.push({
          key: "min_depth",
          label: `奥行き（最小）: ${formatNumber(
            Number(query.min_depth),
          )}${SizeRangeSliderUnit}`,
          value: query.min_depth,
        });
      }

      if (query.max_depth) {
        conditions.push({
          key: "max_depth",
          label: `奥行き（最大）: ${formatNumber(
            Number(query.max_depth),
          )}${SizeRangeSliderUnit}`,
          value: query.max_depth,
        });
      }

      if (query.min_price) {
        conditions.push({
          key: "min_price",
          label: `価格（最小）: ${formatNumber(Number(query.min_price))}円`,
          value: query.min_price,
        });
      }

      if (query.max_price) {
        conditions.push({
          key: "max_price",
          label: `価格（最大）: ${formatNumber(Number(query.max_price))}円`,
          value: query.max_price,
        });
      }

      if (query.in_stock) {
        conditions.push({
          key: "in_stock",
          label: "サンプル請求可能",
          value: query.in_stock,
        });
      }

      if (selectedCategory) {
        conditions.push({
          key: "category_id",
          label: selectedCategory.name,
          value: selectedCategory.id.toString(),
        });
      }

      if (selectedMaterial) {
        conditions.push({
          key: "material_id",
          label: selectedMaterial.name,
          value: selectedMaterial.id.toString(),
        });
      }

      if (selectedSearchConditionGroups) {
        selectedSearchConditionGroups.map((group) => {
          group.values.map((value) => {
            conditions.push({
              key: "search_condition_value_ids",
              label: `${group.label}: ${value.label}`,
              value: value.id.toString(),
            });
          });
        });
      }

      return conditions;
    },
    [
      selectedCategory,
      categoryParentEntities,
      selectedMaterial,
      materialParentEntities,
      selectedSearchConditionGroups,
      query,
    ],
  );

  return searchConditions;
};
export default useProductSearchCondition;
